import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { useToast } from '../hooks/use-toast';
import {
  Loader2, Search, RefreshCw, X, Copy, Check, ShieldCheck, Package,
  Clock, DollarSign, Truck, Ban, Sparkles, ArrowRight, Mail, Bitcoin,
  CheckCircle2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending_payment: { label: 'Waiting for payment', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  confirming:      { label: 'Confirming',          color: 'bg-sky-50 text-sky-700 border-sky-200' },
  partially_paid:  { label: 'Partial payment',     color: 'bg-orange-50 text-orange-700 border-orange-200' },
  paid:            { label: 'Paid. Fulfil now',   color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  delivered:       { label: 'Delivered',           color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expired:         { label: 'Expired',             color: 'bg-rose-50 text-rose-600 border-rose-200' },
  failed:          { label: 'Failed',              color: 'bg-rose-50 text-rose-600 border-rose-200' },
  cancelled:       { label: 'Cancelled',           color: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
  refunded:        { label: 'Refunded',            color: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending_payment;
  return <span className={`text-[10px] font-semibold border rounded px-2 py-0.5 ${m.color}`}>{m.label.toUpperCase()}</span>;
};

const AdminPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('paid');
  const [q, setQ] = useState('');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate('/');
  }, [user, loading, navigate]);

  const fetchStats = async () => {
    try { const { data } = await axios.get(`${API}/admin/stats`); setStats(data); } catch { setStats(null); }
  };
  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const { data } = await axios.get(`${API}/admin/orders`, { params });
      setOrders(data);
    } catch (e) { toast({ title: 'Failed to load orders' }); }
    finally { setLoadingOrders(false); }
  };

  useEffect(() => { if (user?.is_admin) { fetchStats(); fetchOrders(); } /* eslint-disable-next-line */ }, [user, filter]);

  const filtered = useMemo(() => {
    if (!q) return orders;
    const query = q.toLowerCase();
    return orders.filter(o => o.id.toLowerCase().includes(query) || (o.email || '').toLowerCase().includes(query));
  }, [orders, q]);

  if (loading || !user) return <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>;
  if (!user.is_admin) return null;

  const counts = stats?.counts || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[#8247E5]" /> Admin dashboard</h1>
          <p className="text-sm text-neutral-500">Fulfil paid orders by pasting gift codes. Users see them immediately.</p>
        </div>
        <Button variant="outline" onClick={() => { fetchStats(); fetchOrders(); }} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
        <Button variant="outline" onClick={() => navigate('/admin/health')} className="gap-2" data-testid="admin-health-link">
          <ShieldCheck className="w-4 h-4" /> System health
        </Button>
        <Button variant="outline" onClick={() => navigate('/admin/promos')} className="gap-2" data-testid="admin-promos-link">
          <ShieldCheck className="w-4 h-4" /> Promo codes
        </Button>
        <Button variant="outline" onClick={() => navigate('/admin/tickets')} className="gap-2" data-testid="admin-tickets-link">
          <ShieldCheck className="w-4 h-4" /> Support tickets
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <StatCard icon={Clock}   label="Waiting"    value={counts.pending_payment || 0} color="#F59E0B" />
        <StatCard icon={Sparkles} label="Confirming" value={counts.confirming || 0}    color="#0EA5E9" />
        <StatCard icon={ArrowRight} label="Partial" value={counts.partially_paid || 0} color="#EA580C" />
        <StatCard icon={DollarSign} label="Paid" value={counts.paid || 0}              color="#10B981" highlight />
        <StatCard icon={Truck}   label="Delivered"  value={counts.delivered || 0}      color="#8247E5" />
        <StatCard icon={Package} label="Total"      value={stats?.total_orders || 0}    color="#111" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-2 flex-wrap">
            {[
              ['paid',              'Needs fulfilment'],
              ['pending_payment',   'Waiting'],
              ['confirming',        'Confirming'],
              ['partially_paid',    'Partial'],
              ['delivered',         'Delivered'],
              ['expired',           'Expired'],
              ['cancelled',         'Cancelled'],
              ['all',               'All'],
            ].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${filter === k ? 'bg-[#8247E5] text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>
                {l} {k !== 'all' && counts[k] ? <span className="opacity-70 ml-1">{counts[k]}</span> : null}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search id or email" className="pl-10 h-10 w-64" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        {loadingOrders ? (
          <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-neutral-500">
            <Package className="w-8 h-8 mx-auto mb-2 text-neutral-300" /> No orders matching this filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Order</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Items</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-left px-4 py-3">Coin</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filtered.map(o => (
                  <tr key={o.id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-mono font-semibold text-xs">{o.id}</div>
                      <div className="text-[10px] text-neutral-500">{new Date(o.created_at).toLocaleString()}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{o.email}{o.user_id ? <span className="ml-1 text-[10px] text-[#8247E5]">•acct</span> : <span className="ml-1 text-[10px] text-neutral-400">•guest</span>}</td>
                    <td className="px-4 py-3 text-xs text-neutral-700 max-w-xs truncate">
                      {(o.items || []).slice(0, 2).map((it, i) => (<span key={i}>{it.quantity}× {it.brand_name} ${it.denomination}{i === 0 && o.items.length > 1 ? ', ' : ''}</span>))}
                      {o.items?.length > 2 && <span className="text-neutral-400"> +{o.items.length - 2}</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">${(o.subtotal_usd || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs uppercase">{o.payment_pay_currency || o.crypto_currency}</td>
                    <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" onClick={() => setSelected(o)} className="bg-[#8247E5] hover:bg-[#6d33cc] h-8">Open</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <OrderDrawer order={selected} onClose={() => setSelected(null)} onUpdated={(o) => {
          setOrders(prev => prev.map(x => x.id === o.id ? o : x));
          setSelected(o);
          fetchStats();
        }} />
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, highlight }) => (
  <div className={`rounded-2xl border p-4 ${highlight ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200' : 'bg-white border-neutral-200'}`}>
    <div className="flex items-center gap-2 text-xs text-neutral-500"><Icon className="w-3.5 h-3.5" style={{ color }} /> {label}</div>
    <div className="font-display font-bold text-2xl mt-1" style={highlight ? { color: '#059669' } : {}}>{value}</div>
  </div>
);

const OrderDrawer = ({ order, onClose, onUpdated }) => {
  const [codes, setCodes] = useState({});
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    // Init code inputs (one per quantity)
    const init = {};
    (order.items || []).forEach((it, i) => { init[i] = Array.from({ length: it.quantity }, () => ''); });
    setCodes(init);
  }, [order.id]); // eslint-disable-line

  const setCode = (itemIdx, cIdx, val) => {
    setCodes(prev => ({ ...prev, [itemIdx]: prev[itemIdx].map((c, i) => i === cIdx ? val : c) }));
  };

  const fulfill = async () => {
    const payload = { codes: [] };
    Object.entries(codes).forEach(([itemIdx, arr]) => {
      arr.forEach((val) => { if (val && val.trim()) payload.codes.push({ item_index: Number(itemIdx), code: val.trim() }); });
    });
    if (payload.codes.length === 0) { toast({ title: 'Please enter at least one code' }); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${order.id}/fulfill`, payload);
      toast({ title: 'Order fulfilled', description: `${payload.codes.length} code(s) delivered to ${order.email}` });
      onUpdated(data);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/admin/orders/${order.id}/refresh`); onUpdated(data); toast({ title: 'Status refreshed', description: STATUS_META[data.status]?.label }); }
    catch (e) { toast({ title: 'Refresh failed', description: e?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(false); }
  };

  const cancelOrder = async () => {
    if (!window.confirm('Cancel this order?')) return;
    setBusy(true);
    try { const { data } = await axios.post(`${API}/admin/orders/${order.id}/cancel`); onUpdated(data); toast({ title: 'Order cancelled' }); }
    catch (e) { toast({ title: 'Cancel failed', description: e?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(false); }
  };

  const markPaid = async () => {
    if (!window.confirm('Force-mark this order as PAID? This bypasses NOWPayments confirmation and sends the "payment received" email to the customer.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${order.id}/mark-paid`);
      onUpdated(data);
      toast({ title: 'Order marked as paid', description: `${data.email} notified. You can now fulfil the order.` });
    } catch (e) { toast({ title: 'Mark-paid failed', description: e?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(false); }
  };

  const copy = (text, k) => { navigator.clipboard.writeText(text); setCopied(k); setTimeout(() => setCopied(null), 1200); };
  const alreadyFulfilled = (order.gift_codes || []).length > 0 || order.status === 'delivered';

  return (
    <Sheet open={!!order} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between gap-3 mr-6">
            <span className="font-mono">{order.id}</span>
            <StatusPill status={order.status} />
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info icon={Mail} label="Customer email" value={order.email} />
            <Info icon={DollarSign} label="Total" value={`$${order.subtotal_usd.toFixed(2)}`} />
            <Info icon={Bitcoin} label="Coin" value={(order.payment_pay_currency || order.crypto_currency || '').toUpperCase()} />
            <Info icon={Clock} label="Placed" value={new Date(order.created_at).toLocaleString()} />
          </div>

          {order.payment_address && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-3 text-xs">
              <div className="text-neutral-500 mb-1">Deposit address</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono break-all">{order.payment_address}</div>
                <button onClick={() => copy(order.payment_address, 'addr')} className="h-8 w-8 rounded-md bg-white border">
                  {copied === 'addr' ? <Check className="w-3.5 h-3.5 text-green-600 mx-auto" /> : <Copy className="w-3.5 h-3.5 mx-auto" />}
                </button>
              </div>
              <div className="mt-1 text-neutral-500">Expected: <span className="font-mono">{order.payment_amount_crypto} {(order.payment_pay_currency || '').toUpperCase()}</span></div>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">Line items</div>
            <div className="bg-white border border-neutral-200 rounded-xl divide-y">
              {(order.items || []).map((it, i) => (
                <div key={i} className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm">{it.brand_name}</div>
                    <div className="text-xs text-neutral-500">${it.denomination} × {it.quantity}</div>
                  </div>
                  {!alreadyFulfilled ? (
                    <div className="space-y-1.5">
                      {Array.from({ length: it.quantity }).map((_, cIdx) => (
                        <Input key={cIdx} value={codes[i]?.[cIdx] || ''} onChange={(e) => setCode(i, cIdx, e.target.value)}
                          placeholder={`Code ${cIdx + 1} for ${it.brand_name} $${it.denomination}`}
                          className="h-9 text-xs font-mono" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {(order.gift_codes || []).filter(gc => gc.item_index === i).map((gc, k) => (
                        <div key={k} className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg p-2">
                          <div className="flex-1 font-mono text-xs">{gc.code}{gc.pin ? ` · PIN ${gc.pin}` : ''}</div>
                          <button onClick={() => copy(gc.code, `${i}-${k}`)} className="h-7 w-7 rounded-md bg-white border">
                            {copied === `${i}-${k}` ? <Check className="w-3 h-3 text-green-600 mx-auto" /> : <Copy className="w-3 h-3 mx-auto" />}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {!alreadyFulfilled && order.status === 'paid' && (
              <Button onClick={fulfill} disabled={busy} className="flex-1 min-w-[180px] bg-emerald-600 hover:bg-emerald-700">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Truck className="w-4 h-4 mr-1" /> Fulfil & mark delivered</>)}
              </Button>
            )}
            {['pending_payment', 'confirming', 'partially_paid'].includes(order.status) && (
              <Button onClick={markPaid} disabled={busy} data-testid="mark-paid-btn" className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Mark as paid
              </Button>
            )}
            {order.status !== 'delivered' && order.status !== 'cancelled' && (
              <Button variant="outline" onClick={refresh} disabled={busy}><RefreshCw className="w-4 h-4 mr-1" /> Refresh from NOWPayments</Button>
            )}
            {['pending_payment', 'confirming', 'partially_paid', 'paid', 'expired', 'failed'].includes(order.status) && (
              <Button variant="outline" onClick={cancelOrder} disabled={busy} className="text-rose-600 hover:text-rose-700 border-rose-200 hover:border-rose-300"><Ban className="w-4 h-4 mr-1" /> Cancel</Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-3">
    <div className="flex items-center gap-1.5 text-[10px] text-neutral-500 uppercase tracking-wider"><Icon className="w-3 h-3" /> {label}</div>
    <div className="text-sm font-semibold mt-0.5 truncate" title={value}>{value}</div>
  </div>
);

export default AdminPage;
